import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { CLASSIFICATIONS, CLASS_LABEL } from "./Absenteeism";

export default function Certificates() {
  const [searchParams] = useSearchParams();
  const initialCompany = searchParams.get("empresa") || "";
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(initialCompany);
  const [employees, setEmployees] = useState([]);
  const [certs, setCerts] = useState([]);
  const [filters, setFilters] = useState({ employee_id: "", tipo: "", start: "", end: "" });
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      if (!companyId && r.data.length) setCompanyId(r.data[0].id);
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!companyId) return;
    api.get("/employees", { params: { company_id: companyId } }).then((r) => setEmployees(r.data));
  }, [companyId]);

  const load = () => {
    if (!companyId) return;
    const params = { company_id: companyId };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/certificates", { params }).then((r) => setCerts(r.data));
  };
  useEffect(load, [companyId, filters]);  // eslint-disable-line

  const openNew = () => {
    if (!employees.length) { toast.error("Cadastre um colaborador antes."); return; }
    setEditing(null); setShowForm(true);
  };
  const openEdit = (c) => { setEditing(c); setShowForm(true); };
  const onSaved = () => { setShowForm(false); load(); };
  const onDelete = async (c) => {
    if (!window.confirm("Excluir este atestado?")) return;
    try {
      await api.delete(`/certificates/${c.id}`);
      toast.success("Atestado removido.");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  return (
    <div className="space-y-6" data-testid="certificates-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="overline">Absenteísmo</div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 mt-1">Atestados</h1>
          <Link to="/app/absenteismo" className="text-sm text-emerald-800 hover:underline mt-2 inline-block">
            <i className="fa-solid fa-arrow-left mr-2"></i>Voltar ao painel
          </Link>
        </div>
        <button onClick={openNew} className="btn btn-primary" data-testid="certificate-new-btn">
          <i className="fa-solid fa-plus"></i> Novo atestado
        </button>
      </header>

      <div className="card grid md:grid-cols-5 gap-3">
        <label>
          <div className="overline mb-1">Empresa</div>
          <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="w-full border border-slate-300 px-3 py-2 text-sm">
            {companies.map((c) => <option key={c.id} value={c.id}>{c.razao_social || c.nome_fantasia}</option>)}
          </select>
        </label>
        <label>
          <div className="overline mb-1">Colaborador</div>
          <select value={filters.employee_id} onChange={(e) => setFilters({ ...filters, employee_id: e.target.value })}
                  data-testid="cert-filter-employee" className="w-full border border-slate-300 px-3 py-2 text-sm">
            <option value="">Todos</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
          </select>
        </label>
        <label>
          <div className="overline mb-1">Tipo</div>
          <select value={filters.tipo} onChange={(e) => setFilters({ ...filters, tipo: e.target.value })}
                  className="w-full border border-slate-300 px-3 py-2 text-sm">
            <option value="">Todos</option>
            {CLASSIFICATIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </label>
        <label>
          <div className="overline mb-1">De</div>
          <input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })}
                 className="w-full border border-slate-300 px-3 py-2 text-sm" />
        </label>
        <label>
          <div className="overline mb-1">Até</div>
          <input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })}
                 className="w-full border border-slate-300 px-3 py-2 text-sm" />
        </label>
      </div>

      <div className="card overflow-x-auto" data-testid="certificates-table">
        {certs.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <i className="fa-solid fa-file-medical text-4xl mb-3"></i>
            <div className="text-sm">Nenhum atestado cadastrado.</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 border-b">
                <th className="text-left py-2">Colaborador</th>
                <th>Início</th>
                <th>Fim</th>
                <th className="text-right">Dias</th>
                <th>CID</th>
                <th>Tipo</th>
                <th>Anexo</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {certs.map((c) => (
                <tr key={c.id} className="border-b border-slate-100" data-testid={`cert-row-${c.id}`}>
                  <td className="py-2 font-medium">{c.employee_snapshot?.nome || "—"}</td>
                  <td>{c.inicio_afastamento}</td>
                  <td>{c.fim_afastamento}</td>
                  <td className="text-right font-bold">{c.dias_afastados}</td>
                  <td className="font-mono">{c.cid || "—"}</td>
                  <td className="text-xs">{CLASS_LABEL[c.tipo_afastamento] || c.tipo_afastamento}</td>
                  <td>{c.anexo ? <i className="fa-solid fa-paperclip text-emerald-700"></i> : <span className="text-slate-300">—</span>}</td>
                  <td className="text-right">
                    <button onClick={() => openEdit(c)} className="text-emerald-800 hover:underline text-xs mr-3"
                            data-testid={`cert-edit-${c.id}`}>Editar</button>
                    <button onClick={() => onDelete(c)} className="text-rose-700 hover:underline text-xs">Excluir</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <CertificateForm
          companyId={companyId}
          editing={editing}
          employees={employees}
          onClose={() => setShowForm(false)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

function CertificateForm({ companyId, editing, employees, onClose, onSaved }) {
  const [form, setForm] = useState(() => editing || {
    company_id: companyId,
    employee_id: employees[0]?.id || "",
    data_emissao: "",
    inicio_afastamento: "",
    fim_afastamento: "",
    cid: "", medico: "", crm: "",
    tipo_afastamento: "doenca_comum",
    observacoes: "",
    anexo: null,
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const dias = computeDays(form.inicio_afastamento, form.fim_afastamento);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3.5 * 1024 * 1024) {
      toast.error("Anexo maior que 3.5MB. Reduza o arquivo.");
      return;
    }
    const buffer = await file.arrayBuffer();
    const data_base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
    setForm({ ...form, anexo: { name: file.name, mime: file.type, size_bytes: file.size, data_base64 } });
  };

  const save = async () => {
    if (!form.employee_id) { toast.error("Selecione um colaborador."); return; }
    if (!form.inicio_afastamento || !form.fim_afastamento) { toast.error("Informe as datas."); return; }
    if (dias <= 0) { toast.error("Data final deve ser ≥ inicial."); return; }
    setBusy(true);
    try {
      const payload = { ...form, company_id: companyId };
      if (editing) await api.put(`/certificates/${editing.id}`, payload);
      else         await api.post(`/certificates`, payload);
      toast.success(editing ? "Atestado atualizado." : "Atestado registrado.");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="certificate-form-modal">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200">
          <h2 className="font-display text-xl font-extrabold">{editing ? "Editar atestado" : "Novo atestado"}</h2>
          <p className="text-xs text-slate-500 mt-1">
            <i className="fa-solid fa-shield-halved mr-1"></i>
            Dados sensíveis — acesso restrito conforme LGPD. CID pode ser ocultado nos relatórios gerenciais.
          </p>
        </div>
        <div className="p-6 grid md:grid-cols-2 gap-3 text-sm">
          <label className="block md:col-span-2">
            <div className="overline mb-1">Colaborador *</div>
            <select value={form.employee_id} onChange={set("employee_id")}
                    data-testid="cert-field-employee"
                    disabled={Boolean(editing)}
                    className="w-full border border-slate-300 px-3 py-2 disabled:bg-slate-100">
              {employees.map((e) => <option key={e.id} value={e.id}>{e.nome} {e.matricula && `(${e.matricula})`}</option>)}
            </select>
          </label>
          {[
            ["data_emissao", "Data de emissão", "date"],
            ["inicio_afastamento", "Início do afastamento *", "date"],
            ["fim_afastamento", "Fim do afastamento *", "date"],
            ["cid", "CID", "text"],
            ["medico", "Médico", "text"],
            ["crm", "CRM", "text"],
          ].map(([k, label, type]) => (
            <label key={k} className="block">
              <div className="overline mb-1">{label}</div>
              <input type={type} value={form[k] || ""} onChange={set(k)}
                     data-testid={`cert-field-${k}`}
                     className="w-full border border-slate-300 px-3 py-2" />
            </label>
          ))}
          <label className="block md:col-span-2">
            <div className="overline mb-1">Tipo de afastamento *</div>
            <select value={form.tipo_afastamento} onChange={set("tipo_afastamento")}
                    data-testid="cert-field-tipo"
                    className="w-full border border-slate-300 px-3 py-2">
              {CLASSIFICATIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </label>
          <label className="block md:col-span-2">
            <div className="overline mb-1">Observações</div>
            <textarea value={form.observacoes || ""} onChange={set("observacoes")}
                      rows={2} className="w-full border border-slate-300 px-3 py-2" />
          </label>
          <label className="block md:col-span-2">
            <div className="overline mb-1">Anexo (PDF, JPG ou PNG · máx 3.5MB)</div>
            <input type="file" accept="application/pdf,image/jpeg,image/png" onChange={onFileChange}
                   data-testid="cert-field-anexo"
                   className="w-full border border-slate-300 px-3 py-2 text-xs" />
            {form.anexo && (
              <div className="text-xs text-emerald-800 mt-1">
                <i className="fa-solid fa-paperclip mr-1"></i>{form.anexo.name} ({(form.anexo.size_bytes/1024).toFixed(1)}KB)
              </div>
            )}
          </label>
          {dias > 0 && (
            <div className="md:col-span-2 bg-emerald-50 border border-emerald-200 p-3 text-sm">
              <i className="fa-solid fa-calculator mr-2 text-emerald-700"></i>
              <strong>{dias}</strong> dia(s) de afastamento (calculado automaticamente).
              {dias > 15 && <span className="ml-3 text-amber-800"><i className="fa-solid fa-triangle-exclamation"></i> Afastamento &gt; 15 dias — possível encaminhamento ao INSS.</span>}
            </div>
          )}
        </div>
        <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
          <button onClick={onClose} className="btn btn-ghost border border-slate-300">Cancelar</button>
          <button onClick={save} disabled={busy} data-testid="cert-save-btn"
                  className="btn btn-primary disabled:opacity-50">
            {busy ? "Salvando…" : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function computeDays(start, end) {
  if (!start || !end) return 0;
  const a = new Date(start); const b = new Date(end);
  if (b < a) return 0;
  return Math.floor((b - a) / (1000 * 60 * 60 * 24)) + 1;
}
